// CanvasMessage — reusable floating message/notice pill.
//
// Generalized out of the canvas /team-and-agents HelpBox: a small card-themed,
// bordered, rounded pill that floats over a surface and carries arbitrary
// consumer content (help text, a Lottie, a CTA, …) plus an OPTIONAL dismiss
// "×" button.
//
// Nothing is hardcoded to a single consumer:
//   - `dismissKey`  — name of the root state flag that hides the pill once the
//                     user dismisses it. When unset, NO dismiss button renders
//                     and the pill stays put (a non-dismissible notice).
//                     The flag is read off `s.root[dismissKey]` and written
//                     `true` on click, so it persists across SPA navigation.
//   - positioning   — defaults to the bottom-right corner of the nearest
//                     positioned ancestor (`position: absolute`). Any consumer
//                     can override `position` / `top` / `right` / `bottom` /
//                     `left` (e.g. DemoClaimBanner pins it `fixed` bottom-left).
//
// The pill renders whatever children the consumer passes — CanvasMessage owns
// only the chrome (card surface, hairline border, radius, padding) and the
// dismiss affordance.
import { setPref } from '../prefs.js'

export const CanvasMessage = {
  // Floating chrome. `absolute` anchors to the nearest positioned ancestor by
  // default (the canvas Stage in the HelpBox case); consumers needing
  // viewport anchoring override to `fixed`.
  position: 'absolute',
  right: 'A2',
  bottom: 'A2',
  flow: 'y',
  gap: 'V',
  padding: 'Z A',
  round: 'radiusControl',
  theme: 'card',
  // No hairline border (per Nika, on the HelpBox and the tickets tip). The
  // design language separates by FILL contrast — the `card` theme already
  // supplies the surface — and the only sanctioned stroke is the dashed
  // state-ring, which this is not.
  borderWidth: '0',
  fontSize: 'Z2',
  // `caption` on top of the pill's own 0.92 opacity left the text and its
  // icons doubly dimmed. `subtitle` keeps it secondary without washing out.
  color: 'subtitle',
  maxWidth: 'G',
  zIndex: '5',
  opacity: '0.92',

  // Hide once dismissed — only meaningful when a `dismissKey` is supplied.
  // `hide:` (not `if:`) keeps the node in the DOM so a CSS transition can run
  // and so re-showing (consumer clears the flag) doesn't rebuild children.
  hide: (el, s) => !!(el.dismissKey && s.root?.[el.dismissKey]),

  '@mobileL': { display: 'none' },

  // Dismiss "×" — top-right of the pill. Renders ONLY when the consumer wired
  // a `dismissKey`; otherwise the notice is permanent. Clicking sets that flag
  // on root so the `hide:` above retracts the pill.
  // THE house close X (brand CloseButton — F2, one X for every panel): 36px
  // circle, 16px glyph, hover wash; coarse pointers get the 44pt tap slop.
  DismissBtn: {
    extends: 'CloseButton',
    if: (el) => !!el.parent?.dismissKey,
    position: 'absolute',
    top: 'Y',
    right: 'Y',
    ':active': { opacity: '0.7' },
    title: (el) => el.call('polyglot', 'editor.canvasMessage.dismiss'),
    ariaLabel: (el) => el.call('polyglot', 'editor.canvasMessage.dismiss'),
    onClick: (e, el, s) => {
      e.stopPropagation()
      const key = el.parent?.dismissKey
      if (!key) return
      s.root.update({ [key]: true })
      setPref(key, '1')
    }
  }
}
